// Dashboard · HU-34b (MG-ILE-1092), spec `05_analisis_tecnico/Farmacovigilancia/ui/MG-ILE-1092.md`.
//
// Una sola vista para MIRAR, no para trabajar (AF §7f): cuántos casos están activos, cuántos son
// graves, cuántos esperan el cargo de la autoridad, qué plazos aprietan y qué casos esperan un
// paso. Cada caso que se nombra abre su expediente; el trabajo se hace en los módulos de cada rol.
// Lee `GET /tableros/estado` (AT S3 §12.0), una sola vez por apertura.
//
// LA PANTALLA NO CALCULA NADA (`estandares_frontend.md` §8.1 R1). Los conteos, los 12 meses, los 9
// estados, los 5 países, los 3 casos más urgentes con el texto de su plazo y la lista de «Atención
// requerida» llegan hechos. Lo único que se decide aquí es presentación sobre cifras ya contadas:
// el título de la alerta, el tono de una cifra mayor que 0 (glosario) y la proporción del dibujo.
//
// DATO L2 (decisión 2): «Casos con datos de personas» solo existe si la clave viene. Sin permiso
// no viaja, y la tarjeta no se pinta ni enmascarada ni en cero.

import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { ArrowRight, LayoutDashboard, RefreshCw, ShieldOff } from 'lucide-react';
import { Alert, Button, Card, Codigo, EmptyState, KpiCard, Mono, PageHeader } from '../components/ui';
import type { Tono } from '../components/ui';
import ColumnasPorMes, { MarcaDeLaLeyenda } from '../components/ui/ColumnasPorMes';
import BarrasHorizontales from '../components/ui/BarrasHorizontales';
import Bandera from '../components/Banderas';
import TextoDelPlazo from '../components/TextoDelPlazo';
import { consultarEstado } from '../api/tableros';
import type {
  CasoEnRiesgo, CasosDelMes, CifrasDelEstado, ElementoDeAtencion, EstadoDeLaFarmacovigilancia, PlazosDelEstado,
} from '../api/tableros';
import { aFallo } from '../api/cliente';
import type { Fallo } from '../api/cliente';
import { comoFecha, comoFechaYHora } from '../lib/fechas';
import { iconoDelMotivo, tonoDelConteo } from '../lib/etiquetas';

/** Cuántos casos en riesgo lista el servidor (`plazos.mas_urgentes`, 0 a 3). */
const CASOS_EN_RIESGO_LISTADOS = 3;

/** Lo común a los ítems pulsables de casos: a lo ancho y 44 px de alto como mínimo (2.5.8). */
const ITEM_DE_CASO = 'w-full min-h-11';

/** «2 vencidos y 1 por vencer», «1 vencido», «3 por vencer»: solo lo que no es cero. */
function partesDelPlazo({ vencidos, por_vencer: porVencer }: PlazosDelEstado): string {
  const partes: string[] = [];
  if (vencidos > 0) partes.push(`${vencidos} ${vencidos === 1 ? 'vencido' : 'vencidos'}`);
  if (porVencer > 0) partes.push(`${porVencer} por vencer`);
  return partes.join(' y ');
}

/** «3 casos que esperan un paso»; en singular, «1 caso que espera un paso». */
const textoDeLaAtencion = (n: number): string => (n === 1 ? '1 caso que espera un paso' : `${n} casos que esperan un paso`);

/** El dato que sigue al texto del motivo: la presentación del acuse o el tiempo en la etapa. */
function datoDelElemento(e: ElementoDeAtencion): string {
  if (e.fecha_presentacion) return ` · Presentado el ${comoFecha(e.fecha_presentacion)}`;
  if (e.texto_en_la_etapa) return ` · En la etapa: ${e.texto_en_la_etapa}`;
  return '';
}

/**
 * El punto que cierra «Consultado el …». En es-PE la hora termina en «a. m.» o «p. m.», que ya
 * trae su punto: sin esto la línea decía «10:00 a. m..».
 */
const finDeFrase = (texto: string): string => (texto.endsWith('.') ? '' : '.');

/** Un caso de la alerta: número, destinos (o país) y el plazo como la celda de la bandeja. */
function CasoDeLaAlerta({ caso, onAbrirCaso }: Readonly<{ caso: CasoEnRiesgo; onAbrirCaso?: (casoId: string) => void }>) {
  const textoDelPlazo = caso.texto_corto_del_plazo || caso.semaforo.texto;
  return (
    <Button variant="ghost" data-testid="dashboard-alerta-plazos-caso" className={ITEM_DE_CASO}
      aria-label={`Abrir el caso ${caso.numero ?? ''}, ${textoDelPlazo}`} onClick={() => onAbrirCaso?.(caso.caso_id)}>
      <span className="flex w-full flex-wrap items-center justify-between gap-x-4 gap-y-1 text-left font-normal">
        <span className="min-w-0 text-ink">
          <Codigo>{caso.numero}</Codigo>
          <span className="text-ink-soft"> · {caso.texto_de_los_destinos ?? caso.nombre_pais ?? caso.pais}</span>
        </span>
        <TextoDelPlazo plazo={{
          estado: caso.semaforo.estado,
          icono: caso.semaforo.icono,
          textoDelSemaforo: caso.semaforo.texto,
          textoCorto: caso.texto_corto_del_plazo,
          detalle: caso.detalle_del_plazo,
          fiable: caso.plazo_fiable,
        }} />
      </span>
    </Button>
  );
}

/** La alerta de plazos: solo si hay casos activos vencidos o por vencer (HU-34b-C5). */
function AlertaDePlazos({ plazos, onAbrirCaso, onIrABandeja }: Readonly<{
  plazos: PlazosDelEstado;
  onAbrirCaso?: (casoId: string) => void;
  onIrABandeja?: () => void;
}>) {
  // Suma de presentación de dos cifras ya contadas por el servidor.
  const enRiesgo = plazos.vencidos + plazos.por_vencer;
  if (enRiesgo <= 0) return null;
  return (
    <div className="mb-5">
      <Alert data-testid="dashboard-alerta-plazos" tone={plazos.vencidos > 0 ? 'danger' : 'warning'}
        title={`Plazo regulatorio: ${partesDelPlazo(plazos)}`}>
        <ul className="mt-2 flex flex-col gap-2 list-none m-0 p-0">
          {plazos.mas_urgentes.map((caso) => (
            <li key={caso.caso_id}><CasoDeLaAlerta caso={caso} onAbrirCaso={onAbrirCaso} /></li>
          ))}
        </ul>
        {enRiesgo > CASOS_EN_RIESGO_LISTADOS && onIrABandeja && (
          <Button variant="ghost" size="sm" className="mt-3" data-testid="dashboard-alerta-plazos-ver-todos" onClick={onIrABandeja}>
            Ver los {enRiesgo} en la bandeja
          </Button>
        )}
      </Alert>
    </div>
  );
}

/** Una tarjeta de indicador: la cifra y la meta, cada una con su testid. */
function Indicador({ sufijo, etiqueta, valor, meta, tono }: Readonly<{
  sufijo: string; etiqueta: string; valor: number; meta: string; tono?: Tono;
}>) {
  const t = `dashboard-kpi-${sufijo}`;
  return (
    <KpiCard data-testid={t} label={etiqueta} tone={tono}
      value={<span data-testid={`${t}-valor`}>{valor}</span>}
      meta={<span data-testid={`${t}-meta`}>{meta}</span>} />
  );
}

/** La fila de indicadores: 4 con permiso L2, 3 sin él (sin hueco). */
function Indicadores({ cifras }: Readonly<{ cifras: CifrasDelEstado }>) {
  const personas = cifras.casos_con_datos_de_personas;
  const conL2 = typeof personas === 'number';
  return (
    <div className={'grid grid-cols-1 gap-4 mb-5 sm:grid-cols-2 ' + (conL2 ? 'xl:grid-cols-4' : 'xl:grid-cols-3')}>
      <Indicador sufijo="activos" etiqueta="Casos activos" valor={cifras.casos_activos}
        meta={`${cifras.casos_en_total} en total, históricos incluidos`} />
      <Indicador sufijo="graves" etiqueta="Eventos graves" valor={cifras.casos_graves}
        meta="Activos que requieren notificación individual a la autoridad" tono={tonoDelConteo(cifras.casos_graves, 'warning')} />
      <Indicador sufijo="acuses" etiqueta="Acuses pendientes" valor={cifras.acuses_pendientes}
        meta="Notificados sin cargo de la autoridad" tono={tonoDelConteo(cifras.acuses_pendientes, 'danger')} />
      {conL2 && (
        <Indicador sufijo="personas" etiqueta="Casos con datos de personas" valor={personas}
          meta="Acceso restringido y auditado" />
      )}
    </div>
  );
}

/**
 * La tabla de datos del gráfico por mes: siempre en el DOM; visible con «Ver los datos». Oculta, el
 * `sr-only` va en un contenedor y no en la tabla: una `<table>` no respeta el ancho de 1 px y, a
 * 320 px, desbordaba la página aun sin verse (1.4.10).
 */
function TablaDeLosMeses({ id, meses, visible }: Readonly<{ id: string; meses: CasosDelMes[]; visible: boolean }>) {
  const cifra = 'py-1.5 text-right';
  return (
    <div data-testid="dashboard-por-mes-datos" className={visible ? undefined : 'sr-only'}>
      <table id={id} data-testid="dashboard-por-mes-tabla" className="w-full text-body text-ink">
        <caption className="mb-2 text-left text-meta text-ink-soft">Casos por mes, total y graves</caption>
        <thead>
          <tr className="border-b border-hairline text-meta text-ink-soft">
            <th scope="col" className="py-1.5 text-left font-semibold">Mes</th>
            <th scope="col" className={cifra + ' font-semibold'}>Casos</th>
            <th scope="col" className={cifra + ' font-semibold'}>Graves</th>
          </tr>
        </thead>
        <tbody>
          {meses.map((m) => (
            <tr key={m.mes} data-testid="dashboard-por-mes-tabla-fila" className="border-b border-hairline last:border-b-0">
              <th scope="row" className="py-1.5 text-left font-normal">{m.texto}</th>
              <td className={cifra}><Mono>{m.casos}</Mono></td>
              <td className={cifra}><Mono>{m.graves}</Mono></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** «Casos por mes»: 12 columnas apiladas con su tabla de datos (HU-34b-C7, C8, C15). */
function CasosPorMes({ meses }: Readonly<{ meses: CasosDelMes[] }>) {
  const [verDatos, setVerDatos] = useState(false);
  const idTitulo = useId();
  const idTabla = useId();
  const primero = meses.at(0);
  const ultimo = meses.at(-1);
  return (
    <Card data-testid="dashboard-por-mes" className="lg:col-span-2"
      title={<span id={idTitulo}>Casos por mes</span>}
      subtitle={primero && ultimo ? `Total y cuántos resultaron graves, de ${primero.texto} a ${ultimo.texto}` : undefined}
      actions={(
        <Button variant="ghost" size="sm" data-testid="dashboard-por-mes-alternar" aria-pressed={verDatos}
          aria-controls={idTabla} onClick={() => setVerDatos((v) => !v)}>
          {verDatos ? 'Ver el gráfico' : 'Ver los datos'}
        </Button>
      )}>
      {!verDatos && (
        <ColumnasPorMes data-testid="dashboard-por-mes-grafico" aria-labelledby={idTitulo}
          testidDeColumna="dashboard-por-mes-columna"
          columnas={meses.map((m) => ({ clave: m.mes, etiqueta: m.texto.slice(0, 3), total: m.casos, parte: m.graves }))}
          leyenda={(
            <span className="flex flex-wrap items-center gap-4">
              <MarcaDeLaLeyenda clase="fill-marca">Casos</MarcaDeLaLeyenda>
              <MarcaDeLaLeyenda clase="fill-rose-600">Graves</MarcaDeLaLeyenda>
            </span>
          )} />
      )}
      <TablaDeLosMeses id={idTabla} meses={meses} visible={verDatos} />
    </Card>
  );
}

/** Un caso de «Atención requerida»: ícono del motivo, número, texto y su dato. */
function CasoQueEsperaUnPaso({ elemento, onAbrirCaso }: Readonly<{ elemento: ElementoDeAtencion; onAbrirCaso?: (casoId: string) => void }>) {
  const Icono = iconoDelMotivo(elemento.motivo);
  return (
    <Button variant="ghost" data-testid="dashboard-atencion-caso" data-motivo={elemento.motivo} className={ITEM_DE_CASO}
      aria-label={`Abrir el caso ${elemento.numero ?? ''}: ${elemento.texto}`} onClick={() => onAbrirCaso?.(elemento.caso_id)}>
      <span className="flex w-full items-center justify-between gap-3 text-left font-normal">
        <span className="flex min-w-0 items-center gap-3">
          <Icono size={16} aria-hidden="true" className="shrink-0 text-marca-texto" />
          <span className="min-w-0">
            <Codigo className="block text-ink">{elemento.numero}</Codigo>
            <span className="block text-meta text-ink-soft">{elemento.texto}{datoDelElemento(elemento)}</span>
          </span>
        </span>
        <ArrowRight size={14} aria-hidden="true" className="shrink-0" />
      </span>
    </Button>
  );
}

/** «Atención requerida»: todos los elementos, sin tope y en el orden del servidor (HU-34b-C11). */
function AtencionRequerida({ elementos, onAbrirCaso, onIrABandeja }: Readonly<{
  elementos: ElementoDeAtencion[];
  onAbrirCaso?: (casoId: string) => void;
  onIrABandeja?: () => void;
}>) {
  return (
    <Card data-testid="dashboard-atencion" className="lg:col-span-2" title="Atención requerida"
      subtitle={elementos.length > 0 ? textoDeLaAtencion(elementos.length) : undefined}>
      {elementos.length > 0 ? (
        <ul className="flex flex-col gap-2 list-none m-0 p-0">
          {elementos.map((e) => (
            <li key={`${e.motivo}-${e.caso_id}`}><CasoQueEsperaUnPaso elemento={e} onAbrirCaso={onAbrirCaso} /></li>
          ))}
        </ul>
      ) : (
        <p data-testid="dashboard-atencion-vacio" className="text-body text-ink-soft">Nada requiere atención ahora.</p>
      )}
      {onIrABandeja && (
        <Button variant="ghost" size="sm" className="mt-3" data-testid="dashboard-ir-a-bandeja" onClick={onIrABandeja}>
          Ver toda la bandeja
        </Button>
      )}
    </Card>
  );
}

/** Los bloques de la foto ya cargada: alerta, indicadores y las dos filas de tarjetas. */
function FotoDelEstado({ estado, onAbrirCaso, onIrABandeja }: Readonly<{
  estado: EstadoDeLaFarmacovigilancia;
  onAbrirCaso?: (casoId: string) => void;
  onIrABandeja?: () => void;
}>) {
  const idEstado = useId();
  const idPais = useId();
  const { cifras } = estado;
  return (
    // HU-34b-C13: sin casos en el ámbito las cifras se pintan en cero (ceros ciertos), ningún área en blanco.
    <div data-testid={cifras.casos_en_total === 0 ? 'dashboard-vacio' : 'dashboard-exito'}>
      <AlertaDePlazos plazos={estado.plazos} onAbrirCaso={onAbrirCaso} onIrABandeja={onIrABandeja} />
      <Indicadores cifras={cifras} />
      <div className="grid grid-cols-1 gap-4 mb-5 lg:grid-cols-3">
        <CasosPorMes meses={estado.casos_por_mes} />
        <Card data-testid="dashboard-por-estado" title={<span id={idEstado}>Distribución por estado</span>}
          subtitle={`${cifras.casos_en_total} casos del ámbito, cerrados incluidos`}>
          <BarrasHorizontales aria-labelledby={idEstado} nombreDelDato="estado" testidDeBarra="dashboard-por-estado-fila"
            barras={estado.casos_por_estado.map((e) => ({ clave: e.estado, etiqueta: e.texto, valor: e.casos }))} />
        </Card>
      </div>
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <Card data-testid="dashboard-por-pais" title={<span id={idPais}>Casos por país</span>} subtitle="Los 5 con más casos">
          {estado.casos_por_pais.length > 0 ? (
            <BarrasHorizontales aria-labelledby={idPais} nombreDelDato="pais" testidDeBarra="dashboard-por-pais-fila"
              barras={estado.casos_por_pais.map((p) => ({
                clave: p.pais, etiqueta: p.nombre_pais, valor: p.casos, antes: <Bandera cod={p.pais} nombre={p.nombre_pais} />,
              }))} />
          ) : (
            <p data-testid="dashboard-por-pais-vacio" className="text-body text-ink-soft">Todavía no hay casos en tu ámbito.</p>
          )}
        </Card>
        <AtencionRequerida elementos={estado.atencion_requerida} onAbrirCaso={onAbrirCaso} onIrABandeja={onIrABandeja} />
      </div>
    </div>
  );
}

/**
 * Esqueletos de las 4 tarjetas y de los cuatro bloques, sin ninguna cifra. El contenedor es un
 * `<output>` (rol `status` implícito, S6819) en bloque, con el nombre que anuncia la carga.
 */
function Cargando() {
  const caja = 'rounded-2xl border border-borde bg-white';
  return (
    <output data-testid="dashboard-cargando" aria-label="Cargando el estado de la farmacovigilancia" className="block">
      <div className="grid grid-cols-1 gap-4 mb-5 sm:grid-cols-2 xl:grid-cols-4">
        {['activos', 'graves', 'acuses', 'personas'].map((k) => <div key={k} className={caja + ' h-32'} />)}
      </div>
      <div className="grid grid-cols-1 gap-4 mb-5 lg:grid-cols-3">
        <div className={caja + ' h-72 lg:col-span-2'} />
        <div className={caja + ' h-72'} />
      </div>
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <div className={caja + ' h-64'} />
        <div className={caja + ' h-64 lg:col-span-2'} />
      </div>
    </output>
  );
}

export default function Dashboard({ onAbrirCaso, onIrABandeja, onSalir }: Readonly<{
  /** Abre el expediente del caso; «Volver» regresa al Dashboard. */
  onAbrirCaso?: (casoId: string) => void;
  /** Lleva a «Bandeja de casos». Sin el módulo `bandeja`, no se pasa y no se ofrece. */
  onIrABandeja?: () => void;
  /** La salida del estado «sin permiso»: la pantalla de inicio del rol. */
  onSalir?: () => void;
}>) {
  const [estado, setEstado] = useState<EstadoDeLaFarmacovigilancia | null>(null);
  const [cargando, setCargando] = useState(true);
  const [fallo, setFallo] = useState<Fallo | null>(null);
  const [recarga, setRecarga] = useState(0);
  const enCurso = useRef<AbortController | null>(null);

  const cargar = useCallback(async () => {
    enCurso.current?.abort();
    const control = new AbortController();
    enCurso.current = control;
    setCargando(true);
    setFallo(null);
    try {
      const respuesta = await consultarEstado({ senal: control.signal });
      if (!control.signal.aborted) setEstado(respuesta);
    } catch (error) {
      if (control.signal.aborted || (error instanceof Error && error.name === 'AbortError')) return;
      setEstado(null);
      setFallo(aFallo(error));
    } finally {
      if (!control.signal.aborted) setCargando(false);
    }
  }, []);

  useEffect(() => { cargar(); }, [cargar, recarga]);
  useEffect(() => () => enCurso.current?.abort(), []);

  const volverACargar = () => setRecarga((n) => n + 1);
  // RN-82: la foto no es de ningún caso ni país: se lee en Lima.
  const momento = estado ? comoFechaYHora(estado.consultado_en, null) : '';

  if (fallo?.estado === 403) {
    return (
      <EmptyState principal data-testid="dashboard-sin-permiso" icon={ShieldOff}
        title="No tienes acceso al Dashboard" description={fallo.message}
        action={onSalir ? <Button data-testid="dashboard-sin-permiso-salir" onClick={onSalir}>Ir a mi pantalla de inicio</Button> : null} />
    );
  }

  return (
    <>
      <PageHeader
        data-testid="dashboard-encabezado"
        eyebrow="Inicio · Dashboard"
        hu="HU-34b · MG-ILE-1092"
        title="Estado de la farmacovigilancia"
        subtitle="Casos activos, plazos que aprietan y lo que requiere atención, en una sola vista."
        icon={LayoutDashboard}
        actions={(
          <Button variant="secondary" size="sm" icon={RefreshCw} data-testid="dashboard-refrescar" onClick={volverACargar} disabled={cargando}>
            Refrescar
          </Button>
        )}
      />

      {fallo && (
        <Alert data-testid="dashboard-error" tone="danger" title="No se pudo cargar el estado de la farmacovigilancia"
          detalleTecnico={fallo.detalleTecnico}>
          {fallo.message}
          {fallo.estado === 0 && ' Revisa tu conexión e intenta de nuevo.'}
          {/* 422 de ámbito: reintentar no lo arregla (el usuario no tiene ningún país). */}
          {fallo.estado !== 422 && (
            <span className="block mt-2">
              <Button size="sm" variant="secondary" icon={RefreshCw} data-testid="dashboard-reintentar" onClick={volverACargar}>
                Reintentar
              </Button>
            </span>
          )}
        </Alert>
      )}

      {cargando && !fallo && <Cargando />}

      {!cargando && !fallo && estado && (
        <>
          <p data-testid="dashboard-contexto" className="text-meta text-ink-soft mb-4">
            Consultado el <Mono className="text-ink">{momento}</Mono>{finDeFrase(momento)} Solo los casos de tu ámbito.
          </p>
          <FotoDelEstado estado={estado} onAbrirCaso={onAbrirCaso} onIrABandeja={onIrABandeja} />
        </>
      )}
    </>
  );
}
